package com.neurobs.ccab.translation

case class ExistingPhrase
(
  id: Long,
  text: String,
  phraseIndex: Int
)

case class ExistingTest
(
  phraseGroupId: Long,
  testIdentifier: String,
  existingPhrases: List[ExistingPhrase]
)

case class UploadPhrase
(
  phrase: String,
  test: String
)

case class UploadCCAB
(
  uploadPhrases: List[UploadPhrase]
)

case class PhraseDiffData(uploadPhrase: UploadPhrase, phraseIndex: Int, existingPhrase: Option[ExistingPhrase]) {
  def isNew = existingPhrase.isEmpty
}
case class TestDiffData(testIdentifier: String, phraseDiffs: List[PhraseDiffData], outdatedPhrases: List[ExistingPhrase], existingTest: Option[ExistingTest]) {
  def newPhrases = phraseDiffs.filter(_.isNew)
  def isUnchanged = newPhrases.length == 0 && outdatedPhrases.length == 0
  def isNew = !existingTest.isDefined
}
case class CCABDiffData(tests: List[TestDiffData], outdatedTests: List[ExistingTest]) {
  def newTests = tests.filter(_.isNew)
  def alteredTests = tests.filterNot(_.isNew).filterNot(_.isUnchanged)
  def isUnchanged = newTests.length == 0 && alteredTests.length == 0 && outdatedTests.length == 0
}


class CCABDiff(val uploadPhrases: List[UploadPhrase]) {

  def normalizeTestIdentifier(testString: String) = testString.trim().toLowerCase.split(" ").mkString("_")
  val testDiffs = uploadPhrases.groupBy(_.test).map(grouping => new TestDiff(normalizeTestIdentifier(grouping._1), grouping._2)).toList
  def testDiffByIdentifier(testIdentifier: String) = testDiffs.find(_.testIdentifier == testIdentifier)

  var outdatedTests = List[ExistingTest]()
  var diffProcessed = false

  def processExistingTest(test: ExistingTest) = {
    testDiffByIdentifier(test.testIdentifier) match {
      case None => outdatedTests = outdatedTests :+ test
      case Some(testDiff) => testDiff.processExistingTest(test)
    }
  }

  def processExistingTests(existingTests: List[ExistingTest]) = {
    existingTests.foreach(processExistingTest(_))
    diffProcessed = true
  }

  def toRestData = CCABDiffData(testDiffs.map(_.toRestData), outdatedTests)
}

object CCABDiff {
  def apply(uploadCCAB: UploadCCAB): CCABDiff = new CCABDiff(uploadCCAB.uploadPhrases)
}

class TestDiff(val testIdentifier: String, val uploadPhrases: List[UploadPhrase]) {

  val phraseDiffs = uploadPhrases.zipWithIndex.map(pair => new PhraseDiff(pair._1, pair._2))
  var existingTest: Option[ExistingTest] = None
  var diffProcessed = false

  def phraseDiffsByText(text: String): List[PhraseDiff] = phraseDiffs.filter(_.uploadPhrase.phrase == text)

  var outdatedPhrases = List[ExistingPhrase]()

  def processExistingPhrase(existingPhrase: ExistingPhrase) = {
    val matchingPhraseDiffs = phraseDiffsByText(existingPhrase.text)
    if (matchingPhraseDiffs.length == 0) outdatedPhrases = outdatedPhrases :+ existingPhrase
    else matchingPhraseDiffs.foreach(_.existingPhrase = Some(existingPhrase))
  }

  def processExistingTest(test: ExistingTest) = {
    existingTest = Some(test)
    test.existingPhrases.sortBy(_.phraseIndex).foreach(processExistingPhrase(_))
    diffProcessed = true
  }

  def toRestData = TestDiffData(testIdentifier, phraseDiffs.map(_.toRestData), outdatedPhrases, existingTest)
}

class PhraseDiff(val uploadPhrase: UploadPhrase, val phraseIndex: Int) {
  var existingPhrase: Option[ExistingPhrase] = None

  def toRestData = PhraseDiffData(uploadPhrase, phraseIndex, existingPhrase)
}

